// ABOUTME: Axios interceptor to handle admin justification requirements
// ABOUTME: Automatically prompts for justification when API returns 400 with justification message

import api from '../services/api';
import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

// Custom fields the interceptor stamps onto the original request config to track retry state
type AdminJustificationRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _skipJustification?: boolean;
  _justificationProcessing?: boolean;
};

// Custom flag accepted on outgoing request config to skip recursive interception
type SkipJustificationAxiosConfig = AxiosRequestConfig & { _skipJustification?: boolean };

let justificationCallback: ((resourceType: string, resourceId: string | number, resourceName?: string) => Promise<string | null>) | null = null;
let interceptorId: number | null = null;
let justificationCache = new Map<string, string>();
let pendingJustifications = new Map<string, Promise<string | null>>();

export const setJustificationCallback = (callback: ((resourceType: string, resourceId: string | number, resourceName?: string) => Promise<string | null>) | null) => {
  justificationCallback = callback;
};

// Extract resource info from URL
const extractResourceInfo = (url: string): { resourceType: string; resourceId: string } | null => {
  // Match patterns like /api/admin/families/16, /api/behavior/123, etc.
  const patterns = [
    /\/api\/admin\/(families|users|children|behaviors)\/(\d+)/,
    /\/api\/(behavior|goal|child|family)\/(\d+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      // Convert plural admin endpoints to singular for consistency with backend
      let resourceType = match[1];
      if (resourceType === 'families') resourceType = 'family';
      if (resourceType === 'children') resourceType = 'child';
      if (resourceType === 'users') resourceType = 'user';
      if (resourceType === 'behaviors') resourceType = 'behavior';
      
      return {
        resourceType: resourceType,
        resourceId: match[2]
      };
    }
  }
  
  return null;
};

export const setupAdminJustificationInterceptor = () => {
  // Remove existing interceptor to prevent duplicates
  if (interceptorId !== null) {
    api.interceptors.response.eject(interceptorId);
  }
  
  // Response interceptor to handle 400 errors requiring justification
  interceptorId = api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AdminJustificationRequestConfig;
      
      // Check if this is a 400 error with justification required message
      if (
        error.response?.status === 400 &&
        typeof error.response?.data === 'string' &&
        error.response.data.includes('justification required') &&
        originalRequest._retry !== true &&
        originalRequest._skipJustification !== true &&
        originalRequest._justificationProcessing !== true &&
        justificationCallback
      ) {
        originalRequest._retry = true;
        originalRequest._justificationProcessing = true;
        
        // Extract resource info from URL
        const resourceInfo = extractResourceInfo(originalRequest.url || '');
        if (!resourceInfo) {
          return Promise.reject(error);
        }
        
        // Create cache key - use "admin" for all admin requests to share justification across all resource types
        const cacheKey = "admin";
        
        // Check if we already have a cached justification for this resource type
        if (justificationCache.has(cacheKey)) {
          const cachedJust = justificationCache.get(cacheKey)!;
          
          // Add justification header and retry request
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-Admin-Justification'] = cachedJust;
          originalRequest._skipJustification = true;
          
          return api(originalRequest);
        }
        
        // Check if there's already a pending justification request for this resource type
        if (pendingJustifications.has(cacheKey)) {
          const justification = await pendingJustifications.get(cacheKey)!;
          
          if (!justification) {
            return Promise.reject(new Error('Admin justification cancelled'));
          }
          
          // Add justification header and retry request
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-Admin-Justification'] = justification;
          originalRequest._skipJustification = true;
          
          return api(originalRequest);
        }
        
        // Create a new justification request
        const justificationPromise = (async () => {
          try {
            // Try to fetch resource name for better UX
            let resourceName: string | undefined;
            try {
              if (resourceInfo.resourceType === 'family') {
                // Make a direct API call to get family name, with special flag to skip justification
                const response = await api.get<{ name?: string }>(`/api/admin/families/${resourceInfo.resourceId}`, {
                  headers: {
                    'X-Admin-Justification': 'User Support Request' // Temporary header to get the name
                  },
                  _skipJustification: true // Flag to prevent recursive interception
                } as SkipJustificationAxiosConfig);
                resourceName = response.data?.name;
              }
              // Add other resource types as needed
            } catch (nameError) {
              console.warn('Failed to fetch resource name:', nameError);
              // Continue without name
            }
            
            // Request justification from user
            const justification = await justificationCallback!(resourceInfo.resourceType, resourceInfo.resourceId, resourceName);
            
            if (justification) {
              // Cache the justification for 5 minutes for all admin requests
              justificationCache.set(cacheKey, justification);
              setTimeout(() => {
                justificationCache.delete(cacheKey);
              }, 5 * 60 * 1000); // 5 minutes
            }
            
            return justification;
          } finally {
            // Remove from pending requests
            pendingJustifications.delete(cacheKey);
          }
        })();
        
        // Store the pending promise
        pendingJustifications.set(cacheKey, justificationPromise);
        
        // Wait for the justification
        const justification = await justificationPromise;
        
        if (!justification) {
          // User cancelled
          return Promise.reject(new Error('Admin justification cancelled'));
        }
        
        // Add justification header and retry request
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['X-Admin-Justification'] = justification;
        originalRequest._skipJustification = true; // Prevent re-interception of retry
        
        // Log admin justification for audit purposes
        console.log('Admin justification applied:', {
          url: originalRequest.url,
          justification: justification
        });
        
        return api(originalRequest);
      }
      
      return Promise.reject(error);
    }
  );
};